import { type FormEvent, lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import type { HudState } from "./components/GameCanvas";
import { HOLE_ANIMATION_MS } from "./game/ballAnimation";
import { useGameConnection } from "./hooks/useGameConnection";
import { ballMoving, courses, courseVariantIndex, prepare } from "./shared/engine";
import type { PlayerSnapshot } from "./shared/protocol";

const GameCanvas = lazy(async () => ({
  default: (await import("./components/GameCanvas")).GameCanvas,
}));
const INITIAL_HUD: HudState = {
  canShoot: false,
  hint: "Dein Platz auf dem Grün wird vorbereitet.",
  power: 0,
  prompt: "Gleich geht’s los.",
};

function formatTime(seconds: number): string {
  const safe = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
}
function scoreState(player: PlayerSnapshot): string {
  if (!player.connected) return "Offline";
  if (player.finished) return player.reason === "holed" ? "Eingelocht" : "Beendet";
  if (player.ball.airborne) return "Ball im Flug";
  if (player.pending || ballMoving(player.ball)) return "Ball rollt";
  return `${player.strokes} Schläge`;
}
function FlagIcon() {
  return (
    <svg viewBox="0 0 32 40" fill="none" aria-hidden="true">
      <path d="M9 35V5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M10 5c7-5 9 6 18 1v15c-9 5-11-6-18-1V5Z" fill="currentColor" />
      <ellipse cx="10" cy="36" rx="8" ry="2.5" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}
export function App() {
  const game = useGameConnection();
  const [hud, setHud] = useState(INITIAL_HUD);
  const [dialog, setDialog] = useState<"name" | "help" | null>(null);
  const [draftName, setDraftName] = useState("");
  const [groupOpen, setGroupOpen] = useState(false);
  const [resultsVisible, setResultsVisible] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [screenMessage, setScreenMessage] = useState("");
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const snapshot = game.snapshot;
  const courseIndex = snapshot?.courseIndex ?? 0;
  const course = useMemo(
    () =>
      prepare(
        courseIndex,
        courseVariantIndex(snapshot?.room ?? "preview", snapshot?.round ?? 1, courseIndex),
      ),
    [courseIndex, snapshot?.room, snapshot?.round],
  );
  const me = snapshot?.players.find((player) => player.id === game.myId);
  const players = useMemo(
    () =>
      [...(snapshot?.players ?? [])]
        .filter((player) => player.connected)
        .sort((a, b) => a.total - b.total),
    [snapshot],
  );
  const isResults = snapshot?.phase === "results";
  const showResults = isResults && resultsVisible;
  useEffect(() => {
    if (!isResults) {
      setResultsVisible(false);
      return;
    }
    const delay = globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? 0
      : HOLE_ANIMATION_MS + 150;
    const timer = setTimeout(() => setResultsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [isResults]);
  useEffect(() => {
    const element = dialogRef.current;
    if (!element) return;
    if (dialog && !element.open) {
      element.showModal();
      if (dialog === "help") {
        element.querySelector<HTMLElement>("#dialog-title")?.focus({ preventScroll: true });
        element.scrollTop = 0;
      }
    }
    if (!dialog && element.open) element.close();
  }, [dialog]);
  useEffect(() => {
    const update = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", update);
    return () => document.removeEventListener("fullscreenchange", update);
  }, []);
  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (document.documentElement.requestFullscreen)
        await document.documentElement.requestFullscreen();
      else setScreenMessage("Nutze die Vollbildfunktion deines Browsers.");
    } catch {
      setScreenMessage("Vollbild ist hier nicht verfügbar.");
    }
  };
  const openName = () => {
    setDraftName(me?.name ?? game.playerName);
    setDialog("name");
  };
  const saveName = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draftName.trim()) return;
    game.setPlayerName(draftName);
    setDialog(null);
  };
  const stageOverlay = (
    <>
      {(!snapshot || snapshot.phase === "waiting") && (
        <div className="waiting-card" role="status">
          <span className="waiting-flag">
            <FlagIcon />
          </span>
          <h1>Gleich geht’s los.</h1>
          {game.errorMessage && <p>{game.errorMessage}</p>}
          <span className="loading-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        </div>
      )}
      {snapshot?.phase === "countdown" && (
        <div className="countdown" aria-live="polite">
          <b>{Math.max(1, Math.ceil(snapshot.phaseTime))}</b>
        </div>
      )}
    </>
  );
  return (
    <main className="experience">
      <section
        className={`golf-game ${me?.finished ? "finished" : ""}`}
        aria-label="19 Golf Club – Golfwelten"
      >
        <div className="golf-layout" inert={showResults || dialog ? true : undefined}>
          <Suspense
            fallback={
              <div className="golf-stage" aria-busy="true">
                {stageOverlay}
              </div>
            }
          >
            <GameCanvas
              getSnapshot={game.getSnapshot}
              myId={game.myId}
              connected={game.connectionPhase === "connected"}
              showControls={snapshot?.phase === "playing"}
              sendShot={game.sendShot}
              onHudChange={setHud}
            >
              {stageOverlay}
            </GameCanvas>
          </Suspense>
        </div>
        <header className="club-header">
          <div className="club-brand" role="img" aria-label="19 Golf Club">
            <b>
              19<span>°</span>
            </b>
            <span>
              golf
              <br />
              club
            </span>
          </div>
          <nav className="header-actions" aria-label="Spieloptionen">
            <button
              type="button"
              className="icon-button"
              onClick={() => setDialog("help")}
              aria-label="Spielsteuerung erklären"
              title="Steuerung"
            >
              ?
            </button>
            <button
              type="button"
              className="icon-button fullscreen-button"
              onClick={() => void toggleFullscreen()}
              aria-pressed={fullscreen}
              aria-label={fullscreen ? "Vollbild verlassen" : "Vollbild öffnen"}
              title="Vollbild"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </nav>
        </header>
        <section className="course-panel" aria-label="Aktuelle Bahn">
          <h1>{course.name}</h1>
          <div className="course-facts">
            <span>
              Loch <b>{String(courseIndex + 1).padStart(2, "0")}</b>
              <small> / {courses.length}</small>
            </span>
            <span>
              Par <b>{course.par}</b>
            </span>
            <time className={snapshot && snapshot.remaining <= 20 ? "urgent" : ""}>
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M10 5v5l3 2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              {formatTime(snapshot?.remaining ?? 210)}
            </time>
          </div>
        </section>
        <aside className={`group-panel ${groupOpen ? "" : "collapsed"}`}>
          <button
            type="button"
            className="group-heading"
            aria-expanded={groupOpen}
            aria-controls="players"
            onClick={() => setGroupOpen((value) => !value)}
          >
            <span>{players.length} Spieler</span>
            <b aria-hidden="true">{groupOpen ? "−" : "+"}</b>
          </button>
          {groupOpen && (
            <div id="players">
              <div className="score-label">
                <span>Spieler</span>
                <span>Gesamt</span>
              </div>
              <ol className="leaderboard" aria-label="Rangliste">
                {players.map((player, index) => (
                  <li key={player.id} className={player.id === game.myId ? "me" : ""}>
                    <span className="rank">{index + 1}</span>
                    <i className="player-ball" style={{ backgroundColor: player.color }} />
                    <span className="player-info">
                      <span>
                        {player.name}
                        {player.id === game.myId && <em>Du</em>}
                      </span>
                      <small>{scoreState(player)}</small>
                    </span>
                    <b>{player.total}</b>
                  </li>
                ))}
                {!players.length && <li className="empty-flight">Dein Flight wartet auf dich.</li>}
              </ol>
              <button type="button" className="change-name" onClick={openName}>
                Namen ändern
              </button>
            </div>
          )}
        </aside>
        <div className="bottom-shade" />
        {snapshot?.phase === "playing" && (
          <section className="player-card" aria-label="Deine Schläge">
            <div className="stroke-count">
              <b>{me?.strokes ?? 0}</b>
              <span>Schläge</span>
            </div>
          </section>
        )}
        {snapshot?.phase === "playing" && (
          <section
            className={`shot-panel ${hud.power > 0 ? "charging" : ""}`}
            aria-label="Schlagsteuerung"
          >
            <div className="shot-prompt">
              <span className={`ready-dot ${hud.canShoot ? "ready" : ""}`} />
              <h2>{hud.prompt}</h2>
              {hud.power > 0 && (
                <span className="power-value">
                  {Math.round(hud.power * 100)}
                  <small>%</small>
                </span>
              )}
            </div>
            {hud.power > 0 && (
              <div className="power-track">
                <progress
                  max="1"
                  value={hud.power}
                  aria-label={`Schlagstärke ${Math.round(hud.power * 100)} Prozent`}
                />
                <div className="power-ticks" />
              </div>
            )}
            {hud.hint && <p>{hud.hint}</p>}
          </section>
        )}
        {(game.errorMessage || screenMessage) && snapshot && (
          <p className="error-toast" role="alert">
            {screenMessage || game.errorMessage}
            {screenMessage && (
              <button
                type="button"
                onClick={() => setScreenMessage("")}
                aria-label="Hinweis schließen"
              >
                ×
              </button>
            )}
          </p>
        )}
        {showResults && (
          <section
            className="result-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="result-title"
          >
            <div className="result-card">
              <span className="result-flag">
                <FlagIcon />
              </span>
              <p>Loch {courseIndex + 1} abgeschlossen</p>
              <h2 id="result-title">
                {courseIndex === courses.length - 1 ? "Was für eine Runde." : "Schön gespielt."}
              </h2>
              <ol>
                {players.map((player, index) => (
                  <li key={player.id}>
                    <span>{index + 1}</span>
                    <i className="player-ball" style={{ backgroundColor: player.color }} />
                    <span>{player.name}</span>
                    <b>{player.total}</b>
                  </li>
                ))}
              </ol>
              <p>
                {courseIndex === courses.length - 1 ? "Neue Runde" : "Nächstes Loch"} in{" "}
                <b>{Math.max(1, Math.ceil(snapshot.phaseTime))} s</b>
              </p>
            </div>
          </section>
        )}
      </section>
      <p className="visually-hidden" aria-live="polite">
        {game.errorMessage || hud.prompt}
      </p>
      <dialog
        className="game-dialog"
        ref={dialogRef}
        onClose={() => setDialog(null)}
        aria-labelledby="dialog-title"
      >
        {dialog === "name" ? (
          <form onSubmit={saveName}>
            <p className="dialog-kicker">Dein Spielerprofil</p>
            <h2 id="dialog-title">Wie heißt du auf dem Grün?</h2>
            <label htmlFor="player-name">Spielername</label>
            <input
              id="player-name"
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              maxLength={16}
              autoComplete="nickname"
              required
            />
            <div className="dialog-actions">
              <button type="button" onClick={() => setDialog(null)}>
                Abbrechen
              </button>
              <button type="submit" disabled={!draftName.trim()}>
                Speichern
              </button>
            </div>
          </form>
        ) : (
          <>
            <h2 id="dialog-title" tabIndex={-1}>
              So spielst du.
            </h2>
            <p className="help-course">
              <strong>
                {course.name} · {course.difficulty}
              </strong>
              <br />
              {course.hint}
            </p>
            <dl className="help-list">
              <div>
                <dt>Schlagen</dt>
                <dd>
                  Auf das Spielfeld drücken, nach unten ziehen und loslassen. Je weiter du ziehst,
                  desto stärker der Schlag.
                </dd>
              </div>
              <div>
                <dt>Zielen</dt>
                <dd>Beim Ausholen nach links oder rechts ziehen. Mit Escape brichst du ab.</dd>
              </div>
              <div>
                <dt>Sprünge</dt>
                <dd>
                  Goldene Pfeile markieren Schanzen. Bergauf verliert der Ball Tempo, bergab rollt
                  er zurück. Bei einem Sturz geht es mit einem Strafschlag zur letzten sicheren
                  Position.
                </dd>
              </div>
              <div>
                <dt>Umsehen</dt>
                <dd>
                  Rechte Maustaste halten und ziehen. Auf dem Handy „Umsehen“ aktivieren; zum
                  Schlagen wieder ausschalten.
                </dd>
              </div>
              <div>
                <dt>Tastatur</dt>
                <dd>
                  ← → ändern die Richtung, ↑ ↓ die Stärke. Leertaste schlägt. V zeigt die Übersicht,
                  R richtet zur nächsten Etappe oder zum Loch aus.
                </dd>
              </div>
              <div>
                <dt>Dein Flight</dt>
                <dd>
                  Alle starten gemeinsam. Nach dem Einlochen schaust du den anderen zu und kannst
                  den Spieler wechseln. Die nächste Bahn startet automatisch.
                </dd>
              </div>
            </dl>
            <div className="dialog-actions">
              <button type="button" onClick={() => setDialog(null)}>
                Ab aufs Grün
              </button>
            </div>
          </>
        )}
      </dialog>
    </main>
  );
}
